"use client";

import { ApiError, unwrap } from "./errors";

const API_BASE = (process.env.NEXT_PUBLIC_API_BASE ?? "").replace(/\/$/, "");

export interface ClientFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Multipart uploads: pass a FormData and leave body undefined. */
  formData?: FormData;
  searchParams?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const row = part.trim();
    if (row.startsWith(prefix)) return decodeURIComponent(row.slice(prefix.length));
  }
  return undefined;
}

function buildUrl(path: string, searchParams?: ClientFetchOptions["searchParams"]): string {
  const url = new URL(`${API_BASE}${path.startsWith("/") ? path : `/${path}`}`, window.location.origin);
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * Browser-side call to the API through the same origin (dev rewrites / production reverse proxy).
 * Adds the double-submit CSRF header on mutations. Cookies are sent automatically.
 */
export async function apiClient<T>(path: string, options: ClientFetchOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") {
    const csrf = readCookie("ps_csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.searchParams), {
      method,
      headers,
      body,
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw ApiError.network();
  }
  return unwrap<T>(response);
}
